<template>
  <div class="ui-filter popover-filter">
    <UiButton
      ref="mainBtn"
      :filled="!!filtersOpen || hasFilters"
      icon="filter"
      @click="toggleFilters"
    >
      {{ $t('ui.filters.inner.filter') }}
    </UiButton>
    <Transition name="fade">
      <div
        v-show="filtersOpen"
        ref="popup"
        class="pop"
        @keyup.enter.stop
      >
        <div class="arrow" />
        <div class="filter-row">
          <span class="label">{{ $t('affiliates.affiliateKpiBonuses.popup.affiliate') }}</span>
          <UiSelectElement
            ref="affiliateFilter"
            v-model="currentFilters.affiliate"
            :placeholder="$t('affiliates.affiliateKpiBonuses.popup.affiliate')"
            :remote-method="searchMethod"
            :list="affiliateOptions"
            remote
            :loading="loading"
            filterable
            clearable
            option-label-param="email"
            option-name-param="email"
            value-key="id"
            is-value-object
            :error="getError('rule_type')"
            @visible-change="handleToggleSelect"
          />
        </div>
        <div class="filter-row">
          <span class="label">{{ $t('affiliates.affiliateKpiBonuses.popup.kpiType') }}</span>
          <UiSelectElement
            ref="typeFilter"
            v-model="currentFilters.type"
            :placeholder="$t('affiliates.affiliateKpiBonuses.popup.selectKpiType')"
            :list="kpiTypeOptions"
            filterable
            clearable
            option-label-param="text"
            option-name-param="text"
            option-value-param="key"
          />
        </div>
        <div class="controls t:space-x-8">
          <UiButton
            type="negative"
            icon="times"
            @click="resetFilters"
          >
            {{ $t('ui.table.reset') }}
          </UiButton>
          <UiButton
            filled
            :disabled="isDisabledGroup"
            icon="check"
            @click="applyFilters"
          >
            {{ $t('ui.table.apply') }}
          </UiButton>
        </div>
      </div>
    </Transition>
    <span
      v-if="hasFilters"
      class="reset_link"
      @click="resetFilters"
    >
      {{ $t('ui.filters.inner.reset_filter') }}
    </span>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import hash from 'object-hash';
import errorHandleMixin from '@/views/mixins/error-hadle';

const filterFn = (v) => v !== undefined && v !== '' && v !== null;

export default {
  name: 'AffiliatesKpiBonusFilter',
  mixins: [
    errorHandleMixin,
  ],
  props: {
    modelValue: {
      type: Object,
      default() {
        return {};
      },
    },
    propsErrors: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  emits: ['submit', 'toggle'],
  data() {
    return {
      loading: false,
      currentFilters: {},
      affiliateOptions: [],
      filtersOpen: false,
      oldHash: null,
      newHash: null,
      filterValue: '',
    };
  },
  computed: {
    kpiTypeOptions() {
      return this.$store.state.affiliate.kpiBonusTypes.map((el) => ({
        key: el.type,
        text: el.title,
      }));
    },
    hasFilters() {
      return !this.$_.isEmpty(this.$_.pickBy(this.modelValue, filterFn));
    },
    isDisabledGroup() {
      return this.oldHash === this.newHash;
    },
    ...mapGetters('misc', ['countries']),
  },
  watch: {
    currentFilters: {
      deep: true,
      handler(value) {
        const data = {
          ...this.$_.pickBy(value, filterFn),
        };

        this.newHash = hash(data, { algorithm: 'md5' });
      },
    },
  },
  unmounted() {
    document.removeEventListener('click', this.clickOutside, true);
  },
  created() {
    this.currentFilters = this.$_.cloneDeep(this.modelValue);
  },
  mounted() {
    document.addEventListener('click', this.clickOutside, true);
    this.getAffiliates();
  },
  methods: {
    handleToggleSelect(v) {
      if (v) {
        this.loading = true;
        this.getAffiliates('').then(() => {
          this.loading = false;
        });
      }
    },
    searchMethod(v) {
      this.$_.debounce(this.getAffiliates, 800)(v);
    },
    getAffiliates(search) {
      const query = {
        limit: 100,
        offset: 0,
        search,
        account_status: ['approved'],
      };
      return new Promise((resolve, reject) => {
        this.$api.getAffiliates(query)
          .then((response) => {
            const data = this.$_.uniqBy([
                ...(this.modelValue?.affiliate?.id && !search ?
                  [{ id: this.modelValue?.affiliate?.id, email: this.modelValue?.affiliate?.email }] : []
                ),
                ...response.data.payload,
              ], 'id');
            
            this.affiliateOptions = data;
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    clickOutside(e) {
      if (this.$refs.mainBtn.$el?.contains(e.target)) return;
      const filters = [
        this.$refs.popup,
        this.$refs.affiliateFilter.$refs.uiSelectElement.$refs.menuRef.$el,
        this.$refs.typeFilter.$refs.uiSelectElement.$refs.menuRef.$el,
      ].filter((el) => !!el);

      if (this.filtersOpen === true && !filters.some((el) => e.target && el?.contains(e.target))) {
        this.toggleFilters();
      }
    },
    toggleFilters() {
      this.filtersOpen = !this.filtersOpen;
      if (this.filtersOpen) {
        this.currentFilters = this.$_.cloneDeep(this.modelValue);

        const data = {
          ...this.$_.pickBy(this.modelValue, filterFn),
        };

        this.oldHash = hash(data, { algorithm: 'md5' });
      }

      this.$emit('toggle', this.filtersOpen);
    },
    applyFilters() {
      let affiliate = this.currentFilters.affiliate;
      if (affiliate) {
        affiliate = this.$_.pick(affiliate, ['id', 'email']);
      }

      const result = this.$_.pickBy({
        ...this.currentFilters,
        affiliate,
      }, filterFn);

      this.currentFilters = result;
      this.$emit('submit', result);
      this.filtersOpen = false;
    },
    resetFilters() {
      this.currentFilters = {};
      this.$emit('submit', this.currentFilters);
      this.filtersOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.ui-filter {
  position: relative;
  line-height: 0;

  .pop {
    position: absolute;
    width: 476px;
    z-index: 9999;
    top: 44px;
    left: -20px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 16px;
    background-color: #fff;
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    user-select: none;

    &.fade-enter-active,
    &.fade-leave-active {
      transition: all 0.4s;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
      transform: translateY(8px);
    }

    .arrow {
      position: absolute;
      top: -5px;
      left: 56px;
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-bottom: 5px solid #fff;
    }
    .filter-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 54px;
      border-bottom: 1px solid #eaeaea;

      > * {
        flex: 1;
      }

      > div {
        justify-content: flex-end;
      }

      .label {
        font-size: 14px;
        font-weight: 500;
        color: #303634;
      }
    }
    .controls {
      display: inline-flex;
      justify-content: flex-end;
      flex-wrap: nowrap;
      width: auto;
      margin-top: 16px;
      padding: 0;
    }
  }

  .reset_link {
    color: var(--text-negative);
    font-size: 14px;
    line-height: 14px;
    cursor: pointer;
    margin-left: 16px;
  }
}
</style>
